import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, PHOTO_GRADE } from "./types";

// venueChecklist — a wedding checklist over a full-bleed backdrop photo. The
// list sits in a centered semi-transparent white card with a thin bronze line
// frame inset around its perimeter. A blue script title, then ALL-CAPS section
// headings with checkbox rows; flows into 2 columns when the list is long.
//   imageUrl           → backdrop photo (optional; falls back to a warm tone)
//   venue.name / title → script title
//   sections[]         → { heading, items } groups (preferred)
//   items[]            → flat fallback (one untitled section)
const INK = "#34405c";   // checkbox border (structural)
const BRONZE = "rgba(176,141,87,.8)"; // thin frame line
const LINE = "rgba(74,95,134,.3)";
const FALLBACK_BG = "#e7ddcb";

export function venueChecklistTemplate(input: PinInput): string {
  const { brand, typography, venue, items, imageUrl } = input;
  const title = (venue?.name || input.title || "").trim();
  // Fixed title size (consistent across all checklist pins); long titles wrap to
  // the 2-line clamp rather than scaling down.
  const titleSize = 80;
  const ink = input.textColor || "#141414";

  const sections = (input.sections && input.sections.length
    ? input.sections.map((s) => ({ heading: (s.heading || "").trim(), items: (s.items || []).filter(Boolean) }))
    : [{ heading: "", items: (items ?? []).map((i) => i.name).filter(Boolean) }]
  ).filter((s) => s.items.length);
  if (!sections.length) throw new Error("venueChecklist template requires items or sections");

  const total = sections.reduce((n, s) => n + s.items.length, 0);
  // Two columns once the list is long enough to benefit, or whenever there are
  // exactly 4 blocks (those lay out as a tidy aligned 2×2 grid).
  const grid4 = sections.length === 4;
  const cols = total > 9 || grid4 ? 2 : 1;
  const perCol = Math.ceil(total / cols);
  const itemFont = perCol <= 5 ? 52 : perCol <= 8 ? 43 : perCol <= 11 ? 35 : 29;
  const headFont = Math.round(itemFont * 1.0);
  const box = Math.round(itemFont * 0.86);
  const rowPad = perCol <= 6 ? 9 : perCol <= 9 ? 7 : 5;

  // Bullet marker: checkbox (real to-do lists) · dot / number / none (for
  // "mistakes"/"trends" listicles that aren't actually checkable).
  const bulletStyle = input.bulletStyle ?? "checkbox";
  let counter = 0;
  const marker = () =>
    bulletStyle === "none" ? ""
      : bulletStyle === "dot" ? `<span class="dot"></span>`
        : bulletStyle === "number" ? `<span class="num">${++counter}.</span>`
          : `<span class="box"></span>`;
  const rowHtml = (it: string) => `<div class="row">${marker()}<span class="txt">${escapeHtml(it)}</span></div>`;
  const renderSection = (s: { heading: string; items: string[] }) => `
    <div class="section">
      ${s.heading ? `<div class="head">${escapeHtml(s.heading)}</div>` : ""}
      <div class="items">${s.items.map(rowHtml).join("")}</div>
    </div>`;

  // 2 columns: split sections into two balanced groups (by item count); each
  // column distributes evenly to fill the card height. Single flat list: spread
  // the rows themselves so they fill the whole column.
  let bodyHtml: string;
  if (grid4) {
    // Tidy aligned 2×2: blocks top-align per row so the left/right headings and
    // rows line up across the two columns.
    bodyHtml = `<div class="grid4">${sections.map(renderSection).join("")}</div>`;
  } else if (cols === 2) {
    const colA: typeof sections = []; const colB: typeof sections = [];
    let a = 0; let b = 0;
    for (const s of sections) { const w = s.items.length + 1; if (a <= b) { colA.push(s); a += w; } else { colB.push(s); b += w; } }
    bodyHtml = `<div class="col">${colA.map(renderSection).join("")}</div><div class="col">${colB.map(renderSection).join("")}</div>`;
  } else if (sections.length === 1 && !sections[0].heading) {
    bodyHtml = `<div class="col"><div class="items fill">${sections[0].items.map(rowHtml).join("")}</div></div>`;
  } else {
    bodyHtml = `<div class="col">${sections.map(renderSection).join("")}</div>`;
  }

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=EB+Garamond:wght@400;500;600&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:'EB Garamond',${typography.bodyFont},serif;-webkit-font-smoothing:antialiased;background:${imageUrl ? "#2b2520" : FALLBACK_BG}}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;background:${imageUrl ? "#2b2520" : FALLBACK_BG}}
  ${imageUrl ? `.photo{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:center;filter:${PHOTO_GRADE}}
  .photo::after{content:"";position:absolute;inset:0;background:rgba(0,0,0,.18)}` : ""}
  /* Centered semi-transparent card with a thin bronze frame inset. */
  .card{position:absolute;inset:56px;z-index:2;background:rgba(255,255,255,.9);display:flex;flex-direction:column;padding:64px 58px 56px;box-shadow:0 18px 60px rgba(0,0,0,.3)}
  .card::before{content:"";position:absolute;inset:18px;border:1.5px solid ${BRONZE};pointer-events:none}
  .title{font-family:'Bebas Neue',sans-serif;font-size:${titleSize}px;line-height:.98;letter-spacing:2px;text-transform:uppercase;color:${ink};text-align:center;padding-bottom:.04em;flex-shrink:0;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .tdiv{width:200px;height:1px;background:${LINE};margin:10px auto 0;flex-shrink:0}
  .body{flex:1;display:flex;gap:40px;padding:24px 4px 4px}
  .col{flex:1;display:flex;flex-direction:column;justify-content:space-evenly;align-items:center}
  /* 4 blocks → aligned 2×2 grid; rows top-align so left/right line up. */
  .grid4{flex:1;display:grid;grid-template-columns:1fr 1fr;align-content:space-evenly;align-items:start;justify-items:center;column-gap:40px;row-gap:22px;padding-top:4px}
  .section{display:flex;flex-direction:column;align-items:center}
  .head{font-family:'EB Garamond',serif;font-weight:600;font-size:${headFont}px;letter-spacing:3px;text-transform:uppercase;color:${ink};margin-bottom:8px;text-align:center}
  .items{width:fit-content;margin:0 auto}
  .items.fill{display:flex;flex-direction:column;justify-content:space-evenly;align-items:flex-start;flex:1}
  .row{display:flex;align-items:flex-start;gap:13px;padding:${rowPad}px 0}
  .box{width:${box}px;height:${box}px;border:2px solid ${INK};border-radius:3px;flex-shrink:0;margin-top:.16em}
  .dot{width:${Math.round(box * 0.4)}px;height:${Math.round(box * 0.4)}px;border-radius:50%;background:${BRONZE};flex-shrink:0;margin-top:.5em}
  .num{font-family:'EB Garamond',serif;font-weight:600;font-size:${itemFont}px;line-height:1.2;color:${BRONZE};flex-shrink:0;min-width:${Math.round(itemFont * 0.9)}px}
  .txt{font-size:${itemFont}px;line-height:1.2;color:${ink}}
  .brandline{text-align:center;font-family:'EB Garamond',serif;font-size:24px;letter-spacing:4px;text-transform:uppercase;color:${ink};opacity:.72;padding-top:14px;flex-shrink:0}
</style></head>
<body><div class="pin">
  ${imageUrl ? `<div class="photo"></div>` : ""}
  <div class="card">
    ${title ? `<div class="title">${escapeHtml(title)}</div><div class="tdiv"></div>` : ""}
    <div class="body">${bodyHtml}</div>
    <div class="brandline">${escapeHtml(brand || "Sauntera Weddings")}</div>
  </div>
</div></body></html>`;
}
